import React, { useState, useEffect, useCallback } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import EditIcon from '@mui/icons-material/BorderColorOutlined';
import {
    Grid,
    Box,
    Typography,
    Paper,
    CircularProgress,
    Button,
    Tabs,
    Tab,
    Divider
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useSnackbar } from 'notistack';

import { DataAPIContext, GET_ONE } from '../../api';
import { ContextStore } from '../../store/contextStore';
import FixedLinearProgress from '../../components/FixedLinearProgress';
import NavigateBackBar from '../../components/NavigateBackBar';
import PageMeta from '../../components/PageMeta';
import { a11yProps } from '../../components/TabPanel';
import { LANGUAGES } from '../../utils/lang';

const useStyles = makeStyles((theme) => ({
    root: {
        borderRadius: '12px',
        margin: theme.spacing(2),
        marginTop: '2rem',
        border: 'solid 1px #D8D8D8',
        overflow: 'auto',
        backgroundColor: '#FFFFFF',
        position: 'relative',
        // minHeight: `calc(100vh - 4rem)`,
        [theme.breakpoints.down('sm')]: {
            margin: 0,
        },
    },
    container: {
        backgroundColor: '#FCFCFE',
        minHeight: '100vh',
    },
    paper: {
        padding: theme.spacing(3),
        backgroundColor: 'transparent',
        boxShadow: 'none',
        border: 'none',
    },
    content: {
        padding: theme.spacing(2),
        border: '1px solid #D8D8D8',
        borderTop: 'none',
        borderRadius: '0 0 15px 15px',
        backgroundColor: '#FFFFFF',
        '& img': {
            maxWidth: '100%',
        },
    },
    actions: {
        display: 'flex',
        justifyContent: 'flex-start',
        marginTop: theme.spacing(2),
    },
    tabsMenu: {
        border: '1px solid #d8d8d8',
        borderRadius: '15px 15px 0 0 !important',
        [theme.breakpoints.down('sm')]: {
            border: 'none',
            paddingTop: '0.35rem',
        },
    },
}));

const WaiverPreview = () => {
    const classes = useStyles();
    const { id } = useParams();
    const history = useHistory();
    const dataAPI = React.useContext(DataAPIContext);
    const { enqueueSnackbar } = useSnackbar();
    const { hasPerm } = React.useContext(ContextStore);
    const hasReadPerm = hasPerm('waiver', 'read');
    const hasWritePerm = hasPerm('waiver', 'write');

    const [waiver, setWaiver] = useState(null);
    const [loading, setLoading] = useState(true);
    const [currentLanguageTab, setCurrentLanguageTab] = useState(0);

    const loadWaiver = useCallback(async () => {
        if (!hasReadPerm) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const res = await dataAPI(GET_ONE, 'waiver', { id });
            if (res && res.data) {
                // Convert LocalizedTextSetField format to languages_data format for preview
                const languagesData = [];
                if (res.data.text && res.data.text.values) {
                    Object.entries(res.data.text.values).forEach(([langCode, content]) => {
                        languagesData.push({
                            language: langCode,
                            content: content,
                            checkboxLabel: '' // Checkbox labels are not stored in backend yet
                        });
                    });
                }

                setWaiver({
                    ...res.data,
                    languages_data: languagesData
                });
            } else {
                enqueueSnackbar('Waiver not found', { variant: 'error' });
                history.push('/waivers');
            }
        } catch (error) {
            enqueueSnackbar('Error loading waiver data', { variant: 'error' });
            history.push('/waivers');
        } finally {
            setLoading(false);
        }
    }, [id, dataAPI, enqueueSnackbar, history, hasReadPerm]);

    useEffect(() => {
        loadWaiver();
    }, [loadWaiver]);



    const handleLanguageTabChange = (event, newValue) => {
        setCurrentLanguageTab(newValue);
    };

    if (!hasReadPerm) {
        return (
            <>
                <PageMeta title="Waiver Preview" />
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                    <Typography>You do not have permission to view this waiver.</Typography>
                </Box>
            </>
        );
    }

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <CircularProgress />
            </Box>
        );
    }

    if (!waiver) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <Typography>Waiver not found</Typography>
            </Box>
        );
    }

    const languages = waiver.languages_data || [];
    const currentLanguage = languages[currentLanguageTab] || {};

    // Create language names map from LANGUAGES array
    const languageNames = LANGUAGES.reduce((acc, lang) => {
        acc[lang.code] = lang.name;
        return acc;
    }, {});

    return (
        <>
            <PageMeta title="Waiver Preview" />

            <Box className={classes.container}>
                <div className={classes.root}>
                    <Grid container justifyContent="space-between" alignItems="center">
                        <FixedLinearProgress visibility={loading} />
                        <NavigateBackBar
                            label="Waiver Preview"
                            url="/waivers"
                            labelStyle={{
                                fontWeight: 500,
                                fontSize: '16px'
                            }}
                        />

                        {hasWritePerm && (
                            <Button
                                startIcon={<EditIcon />}
                                variant="contained"
                                color="primary"
                                onClick={() => history.push(`/waivers/${id}`)}
                                sx={{
                                    mx: 2,
                                    backgroundColor: '#007AFF',
                                    textTransform: 'uppercase',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                }}
                            >
                                Edit Waiver
                            </Button>
                        )}
                    </Grid>

                    <Divider />

                    <Paper className={classes.paper}>
                        <Typography variant="h6">{waiver.name}</Typography>
                        {waiver.description && (
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                                {waiver.description}
                            </Typography>
                        )}

                        <Box mt={2} mb={2}>
                            <Typography variant="body2" color="textSecondary">
                                Languages: <strong>{languages.map(lang => languageNames[lang.language] || lang.language).join(', ')}</strong>
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Used in trips: <strong>{waiver.trip_count || 0}</strong>
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Signatures: <strong>{waiver.signature_count || 0}</strong>
                            </Typography>
                            {waiver.created_ts && (
                                <Typography variant="body2" color="textSecondary">
                                    Created: <strong>{new Date(waiver.created_ts).toLocaleString()}</strong>
                                </Typography>
                            )}
                            {waiver.updated_ts && (
                                <Typography variant="body2" color="textSecondary">
                                    Last updated: <strong>{new Date(waiver.updated_ts).toLocaleString()}</strong>
                                </Typography>
                            )}
                        </Box>

                        {languages.length > 0 ? (
                            <Box mt={2}>
                                <Tabs
                                    value={currentLanguageTab}
                                    onChange={handleLanguageTabChange}
                                    variant="scrollable"
                                    scrollButtons="auto"
                                    indicatorColor="primary"
                                    textColor="primary"
                                    className={classes.tabsMenu}
                                >
                                    {languages.map((lang, index) => (
                                        <Tab
                                            key={index}
                                            label={languageNames[lang.language] || lang.language}
                                            {...a11yProps(index)}
                                        />
                                    ))}
                                </Tabs>

                                <Box className={classes.content}>
                                    <div dangerouslySetInnerHTML={{ __html: currentLanguage.content }} />
                                </Box>
                            </Box>
                        ) : (
                            <Typography variant="body1" sx={{ mt: 2 }}>No language content available</Typography>
                        )}
                    </Paper>
                </div>
            </Box>
        </>
    );
};

export default WaiverPreview; 